import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import type {
  ConfigureRemoteSpeakerRequestDto,
  ConfigureRemoteSpeakerResponseDto,
} from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';

export function useConfigureRemoteSpeakerMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ConfigureRemoteSpeakerRequestDto & { eventId: string; contributorId: string }) => {
      const token = await getToken();
      const { eventId, contributorId, ...body } = payload;
      const url = `${import.meta.env.VITE_API_URL}/contributors/event/${eventId}/${contributorId}/remote-speaker`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body,
        token: token ?? '',
      }) as Promise<ConfigureRemoteSpeakerResponseDto>;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
      ]);
    },
  });
}

export function useAssignRemoteSpeakerQuotaMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { eventId: string; contributorId: string; dayIds: string[] }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/contributors/event/${payload.eventId}/${payload.contributorId}/remote-speaker/assign-quota`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: { dayIds: payload.dayIds },
        token: token ?? '',
      }) as Promise<ConfigureRemoteSpeakerResponseDto>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });
    },
  });
}
